import { Fragment, useContext, useEffect, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import {
  ROUTE_SETTINGS_ENTITY,
  ROUTE_SETTINGS_ORGANISATIONS
} from 'routes/RouteList';
import { FormattedMessage } from 'react-intl';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import Breadcrumb from 'components/breadcrumbs/Breadcrumb';
import Card from 'components/card/Card';
import { MenuWrapper } from 'components/menu';
import { QUERY_GET_ORGANISATION_BY_ID } from 'services/aws/entity-query';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleWrapper
} from 'components/panel';
import RemoveOrganisationButton from 'containers/partials/buttons/RemoveOrganisationButton';
import messages from 'messages';
import Loader from 'components/loader/Loader';
import { useQuery } from '@apollo/client';
import { StoreContext } from 'index';
import OrganisationEntity from 'models/Organisation';
import OrganisationForm from 'containers/pages/settings/organisations/OrganisationForm';
import OrganisationFormInput from 'containers/pages/settings/organisations/OrganisationFormInput';
import OrganisationSettings from 'containers/pages/settings/organisations/OrganisationSettings';
import FormEditFooterButtons from 'containers/partials/buttons/FormEditFooterButtons';
import useEdit from 'hooks/utils/useEdit';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';

const Organisation = () => {
  const ability = useAbility(AbilityContext);
  const { push } = useHistory();
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId, organisationId } = useParams();
  const [edit, setEdit] = useEdit(true, true);
  const [organisation, setOrganisation] = useState(
    new OrganisationEntity({ baseLanguage: user.baseLanguage })
  );

  const { data, loading, error } = useQuery(QUERY_GET_ORGANISATION_BY_ID, {
    variables: { id: organisationId },
    skip: !organisationId
  });

  useEffect(() => {
    if (data?.getEntity) {
      setOrganisation(new OrganisationEntity(data.getEntity));
    }
  }, [data]);

  return (
    <Panel>
      {loading && <Loader />}
      {error && <RetryPanel />}

      <Fragment>
        <PanelHeader borderBottom>
          <Breadcrumbs>
            <Breadcrumb
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_ENTITY, {
                    entityId
                  })
                )
              }
            >
              <FormattedMessage {...messages.breadcrumbSettings} />
            </Breadcrumb>
            <Breadcrumb
              onClick={() =>
                push(
                  generatePath(ROUTE_SETTINGS_ORGANISATIONS, {
                    entityId
                  })
                )
              }
            >
              <FormattedMessage {...messages.breadcrumbOrganisations} />
            </Breadcrumb>
          </Breadcrumbs>
          <PanelTitleWrapper>
            <PanelTitle>
              {organisation.name ? (
                `${organisation.name}`
              ) : (
                <FormattedMessage {...messages.organisationsAddTitle} />
              )}
              <MenuWrapper trigger={<PanelTitleButton />}>
                {ability.can('delete', 'Organisations') && (
                  <RemoveOrganisationButton
                    menu
                    parentId={entityId}
                    organisationId={organisationId}
                  >
                    <FormattedMessage
                      {...messages.organisationsMenuItemRemoveOrganisation}
                    />
                  </RemoveOrganisationButton>
                )}
              </MenuWrapper>
            </PanelTitle>
            <PanelSubtitle>
              <FormattedMessage {...messages.organisationsAddSubTitle} />
            </PanelSubtitle>
          </PanelTitleWrapper>
        </PanelHeader>
        <PanelBody>
          <OrganisationForm
            organisationId={organisationId}
            organisation={organisation}
            onComplete={() => setEdit(false)}
            allowFeatureEdit={true}
          >
            {({ submitForm, isValid, isSubmitting, resetForm }) => (
              <Card secondary multiBody>
                <OrganisationFormInput
                  organisation={organisation}
                  edit={edit}
                />
                <OrganisationSettings edit={edit} />
                <FormEditFooterButtons
                  edit={edit}
                  setEdit={setEdit}
                  onCancel={() => {
                    resetForm();
                  }}
                  onSubmit={submitForm}
                  disabledSubmit={!isValid || isSubmitting}
                  topMargin
                  extraClassNames={'c-card__footer-bottom-fixed'}
                />
              </Card>
            )}
          </OrganisationForm>
        </PanelBody>
      </Fragment>
    </Panel>
  );
};

export default Organisation;
